import React from 'react'
import AiHero from '../../Components/Automation/AiHero'
import { ArrowRight } from 'lucide-react'
import { motion } from 'framer-motion';
import Banner from '../../Components/Banner';

import { Helmet } from 'react-helmet-async';

const toolsData = [
    {
        id: 1,
        title: "Comprehensive Support",
        desc: "Reliable support with strong data privacy.",
    },
    {
        id: 2,
        title: "Industry Expertise",
        desc: "Trusted experience across 12+ industries",
    },
    {
        id: 3,
        title: "Customized Solutions",
        desc: "Solutions built for you, delivered exactly when you need them.",
    },
];
const outcomesData = [
    {
        id: 1,
        text: "Faster, data-driven decision-making",
    },
    {
        id: 2,
        text: "Single source of truth across the enterprise",
    },
    {
        id: 3,
        text: "Predictive insights & automation",
    },
    {
        id: 4,
        text: "Improved efficiency, cost control & revenue growth",
    },
];

const whyMobikodePoints = [
    "Financial Services",
    "eCommerce",
    "Insurance",
    "Manufacturing",
    "Retail & Consumer Goods",
    "Healthcare & Life Sciences",
    "Media & Communications",
    "Logistics & Transportation",
    "Technology",
    "Non Profit",
    "Professional Services",
    "Real Estate",
    "Energy & Utilities",
    "Education",
];

const approachData = [
    {
        id: 1,
        step: "01",
        title: "ServiceNow Consulting Services",
        desc: "At Mobikode, we tailor ServiceNow to fit your business needs and create a clear, practical roadmap that supports your digital transformation in a simple, effective way.",
    },
    {
        id: 2,
        step: "02",
        title: "ServiceNow Implementation Services",
        desc: "At Mobikode, we take care of everything from start to finish. From understanding your processes and designing the solution, to setup, customization, data migration, integration, testing, and final deployment we make sure everything works smoothly and fits your business needs perfectly.",
    },
    {
        id: 3,
        step: "03",
        title: "ServiceNow Migration Services",
        desc: "Upgrade from legacy systems or improve your existing ServiceNow setup to take full advantage of its latest features, better performance, and smarter capabilities helping your business run smoother and more efficiently.",
    },
    {
        id: 4,
        step: "04",
        title: "ServiceNow Managed Services",
        desc: "We provide end-to-end ServiceNow support from advisory and implementation to ongoing optimization helping you get the most value from your platform.",
    },
    {
        id: 5,
        step: "05",
        title: "ServiceNow Integration Services",
        desc: "Use ServiceNow APIs to easily connect different systems, improve teamwork, and make everyday work more productive and smooth.",
    },
    {
        id: 6,
        step: "06",
        title: "Ideation & Development",
        desc: "Work closely with stakeholders to understand how the business runs and turn that into a clear, practical development roadmap.",
    },
    {
        id: 7,
        step: "07",
        title: "ServiceNow Customization Services",
        desc: "Expert in both declarative and programmatic customization, we tailor solutions to your needs while keeping the core code clean and unchanged.",
    },
    {
        id: 8,
        step: "08",
        title: "ServiceNow App Development",
        desc: "We design, build, test, and deploy custom applications using ServiceNow’s scoped development model, focusing on solutions that fit your business needs and improve everyday efficiency.",
    },
    {
        id: 9,
        step: "09",
        title: "Workflow Optimization",
        desc: "Boost productivity by simplifying workflows and cutting out unnecessary, repetitive work.",
    },
];

function ConsultingService() {
    return (
        <div>
            <Helmet>
    <title>
        ServiceNow Consulting Services | Implementation & Integration | Mobikode
    </title>

    <meta
        name="description"
        content="Mobikode provides ServiceNow consulting, implementation, migration, managed services, integration, customization, app development, and workflow optimization to help businesses streamline operations and accelerate digital transformation."
    />

    <meta
        name="keywords"
        content="ServiceNow Consulting Services, ServiceNow Consulting Company, ServiceNow Implementation Services, ServiceNow Migration Services, ServiceNow Managed Services, ServiceNow Integration Services, ServiceNow Customization Services, ServiceNow App Development, ServiceNow Workflow Optimization, ServiceNow ITSM Consulting, ServiceNow Digital Transformation, ServiceNow Solutions"
    />

    <link
        rel="canonical"
        href="https://www.mobikode.com/servicenow-consulting"
    />

    <meta
        property="og:title"
        content="ServiceNow Consulting Services | Implementation & Integration | Mobikode"
    />

    <meta
        property="og:description"
        content="Transform business operations with Mobikode's ServiceNow consulting services. From strategy and implementation to migration, integration, customization, managed services, and workflow optimization."
    />

    <meta
        property="og:url"
        content="https://www.mobikode.com/servicenow-consulting"
    />

    <meta
        property="og:type"
        content="website"
    />

    <meta
        property="og:image"
        content="https://www.mobikode.com/assets/service-detail/img15.png"
    />

    <meta
        name="twitter:card"
        content="summary_large_image"
    />

    <meta
        name="twitter:title"
        content="ServiceNow Consulting Services | Implementation & Integration | Mobikode"
    />

    <meta
        name="twitter:description"
        content="Explore Mobikode's ServiceNow expertise across consulting, implementation, migration, integration, customization, managed services, application development, and workflow optimization."
    />

    <meta
        name="twitter:image"
        content="https://www.mobikode.com/assets/service-detail/img15.png"
    />
</Helmet>
            <AiHero title="Leverage ServiceNow Consulting" text=" " imgData="/assets/service-detail/img15.png" />
            <section className="w-full pt-16 lg:pt-[100px] bg-white overflow-hidden relative">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px]">
                    <div className="relative w-full flex flex-col lg:flex-row lg:items-center group lg:h-[507px]">
                        <div className="relative lg:absolute top-0 right-0 w-full lg:max-w-[656px] h-[250px] md:h-[350px] lg:h-full bg-gray-100 overflow-hidden">
                            <img
                                src="/assets/v/item9.png"
                                alt="ai automation"
                                className="w-full h-full object-cover mix-blend-multiply transition-transform duration-700 group-hover:scale-105"
                            />
                        </div>
                        <div className="relative z-20 w-[92%] sm:w-[85%] lg:w-full max-w-[719px] mx-auto lg:mx-0 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.1)] px-[20px] py-[30px] lg:px-[60px] lg:py-[80px] -mt-[40px] lg:mt-0">
                            <h3 className="text-[#01386A] text-[20px] lg:text-[30px] font-[600] font-sora lg:mb-[35px] mb-[20px] leading-[1.3]">
                                ServiceNow Consulting
                            </h3>
                            <p className="text-[#49577A] w-full text-[14px] lg:text-[16px] leading-[1.6] font-graphik font-[500] text-justify">
                                Boost your business performance with Mobikode ServiceNow consulting services. We take time to understand your goals and tailor ServiceNow to fit your real needs, creating a clear and practical roadmap for smooth digital transformation and lasting operational improvement.

                            </p>
                            <div className="absolute bottom-0 left-0 w-full h-[4px] bg-[#01386A]"></div>
                            <div className="absolute bottom-0 left-0 h-[4px] bg-[#ED6800] w-0 transition-all duration-500 ease-out group-hover:w-full"></div>
                        </div>
                    </div>
                </div>
            </section>
            {/* <WhyAIAutomation title="What We Do" fData={featuresData} /> */}
            {/* <TechServicesSection title="Explore Our Services" sData={services} /> */}
            {/* <IndustriesWeServe /> */}
            <section className="w-full bg-[#FDFDFD] pb-16 lg:pb-24 relative mt-[20px] overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">
                    <div className="mb-[30px] lg:mb-[60px]">
                        <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">
                            ServiceNow Consulting
                        </h2>
                        {/* <p className="text-[#49577A] mt-2 text-left font-graphik text-[16px]">
                            A structured path from raw data to business optimization.
                        </p> */}
                    </div>

                    {/* Grid for Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {approachData.map((item) => (
                            <div
                                key={item.id}
                                className="relative bg-[#F0F0F0] p-8 min-h-[180px] flex flex-col justify-start overflow-hidden group transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                            >
                                <div className="relative z-10">
                                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2 leading-tight">
                                        {item.title}
                                    </h3>
                                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-graphik font-[500] leading-snug">
                                        {item.desc}
                                    </p>
                                </div>
                                <div
                                    className="absolute bottom-0 right-0 w-[180px] h-[140px] pointer-events-none transition-transform duration-500 group-hover:scale-110 opacity-50 lg:opacity-100"
                                >
                                    <img src="/assets/career/wrap1.svg" alt="" className="w-full h-full object-contain object-bottom-right" />
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </section>
            <section className="w-full bg-[#FDFDFD] py-16 lg:py-24 relative">
                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10">

                    {/* Main Flex Container to split left (Title) and right (Cards) */}
                    <div className="flex flex-col lg:flex-row gap-10 lg:gap-20 items-start">

                        {/* Left Side: Title Section */}
                        <div className="w-full lg:w-[40%] lg:sticky lg:top-24">
                            <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left">
                                Maximize Efficiency with ServiceNow
                            </h2>
                        </div>

                        {/* Right Side: Cards List Section */}
                        <div className="w-full lg:w-[60%] flex flex-col gap-6">
                            {toolsData.map((item) => (
                                <div
                                    key={item.id}
                                    className="bg-white border border-[#E2E8F0] border-b-[4px] border-b-[#01386A] hover:border-b-[#e86c15] transition-colors duration-300 p-8 lg:px-10 lg:py-10 flex flex-col justify-center cursor-pointer group"
                                >
                                    <h3 className="text-[#01386A] text-[16px] lg:text-[20px] font-[600] font-sora mb-2">
                                        {item.title}
                                    </h3>
                                    <p className="text-[#49577A] text-[14px] lg:text-[16px] font-[500] font-graphik leading-snug">
                                        {item.desc}
                                    </p>
                                </div>
                            ))}
                        </div>

                    </div>

                </div>
            </section>
            <section className="w-full bg-white pb-10 lg:pb-16 relative ">
                {/* <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#F06115] opacity-[0.05] rounded-full blur-[120px] translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#01386A] opacity-[0.05] rounded-full blur-[100px] -translate-x-1/3 translate-y-1/3 pointer-events-none"></div> */}

                <div className="max-w-[1440px] mx-auto px-[20px] lg:px-[80px] relative z-10 flex flex-col lg:flex-row gap-12 lg:gap-20 items-center lg:items-start">

                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="w-full lg:w-5/12 text-left lg:sticky lg:top-32"
                    >
                        <h2 className="text-[#01386A] font-sora text-[20px] lg:text-[30px] font-[600] text-left mb-6">
                            Industries We Serve
                        </h2>
                        <p className="text-[#49577A] font-graphik font-[500] text-[14px] lg:text-[16px] leading-relaxed max-w-[400px]">
                            Leveraging deep domain expertise to deliver tailored, future-ready solutions that accelerate digital transformation across diverse sectors.
                        </p>
                    </motion.div>

                    <div className="w-full lg:w-7/12">
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-5">
                            {whyMobikodePoints.map((point, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: index * 0.05 }}
                                    className="group bg-white border border-gray-100 p-6 lg:p-8 hover:border-[#F06115] transition-all duration-300 cursor-pointer flex items-center justify-center text-center shadow-sm hover:-translate-y-2 hover:shadow-[0_15px_35px_rgba(240,97,21,0.1)]"
                                >
                                    <span className="text-[#01386A] font-graphik text-[14px] lg:text-[16px] font-[500] group-hover:text-[#F06115] transition-colors duration-300">
                                        {point}
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                </div>
            </section>
            {/* <section className="relative w-full h-[400px] lg:h-[507px] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/career/img12.png"
                        alt="ServiceNow Consulting Background"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/10"></div>
                </div>
                <div className="relative z-10 w-full max-w-[1440px] mx-auto px-[20px] lg:px-[80px] h-full flex items-center lg:items-center ">
                    <div className="bg-white p-8 lg:p-12 max-w-[588px] shadow-2xl rounded-sm">

                        <h2 className="text-[#01386A] text-[30px] lg:text-[40px] font-[600] font-sora leading-tight mb-6">
                            ServiceNow Consulting Services
                        </h2>
                        <p className="text-[#49577A] text-[16px] leading-[24px] font-graphik mb-8">
                            Accelerate digital transformation and streamline IT operations with our expert ServiceNow implementation and strategy solutions.
                        </p>
                        <a href="/contact" className="inline-flex items-center gap-2 text-[#01386A] underline font-graphik font-semibold text-lg hover:gap-3 transition-all duration-300 group">
                            Contact Now
                            <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                        </a>

                    </div>

                </div>

            </section> */}
            <Banner title="Leverage ServiceNow Consulting" text=" Empowering businesses with intelligent, autonomous AI solutions that drive efficiency and innovation." />
        </div>
    )
}

export default ConsultingService
